import React, { useState } from 'react'
import { orderBy, times } from 'lodash'
import { BsPinFill, BsCheck2All } from 'react-icons/bs'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import { useAuth } from 'modules/Auth/hooks/useAuth'

import { buttonBase, buttonSizes, buttonVariants } from 'common/components/Button/Button'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { EmptyStateContainer } from 'common/components/EmptyStateContainer/EmptyStateContainer'

import {
  useListenBannedMembersQuery,
  useListenCommunityPostsQuery,
  useListenMyCommunityPostsQuery,
  useListenNeedsReplyPostsQuery,
  useListenOlderCommunityPostsQuery,
  useListenOlderMyCommunityPostsQuery,
  useMarkPostsDoneMutation,
  useListenCommunityEnabledQuery,
  useSetCommunityEnabledMutation,
} from './communityApi'
import {
  filterVisiblePosts,
  formatBadgeCount,
  normalizeCommunityRecords,
  isPostPinned,
  needsCoachReply,
} from './communityUtils'
import usePostPager from './usePostPager'
import CommunityPostForm from './CommunityPostForm/CommunityPostForm'
import Post from './components/Post'
import { PostSkeleton } from './PostSkeleton'

const TAB_ALL = 'all'
const TAB_MINE = 'mine'
const TAB_NEEDS_REPLY = 'needsReply'

// What each paged list is ordered by — the field its Firebase query orders on.
const orderByCreatedAt = (post) => (typeof post?.createdAt === 'number' ? post.createdAt : null)
const orderByKey = (_post, id) => id

function Community() {
  const { createAlert } = useAlert()
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const { data: communityEnabledData } = useListenCommunityEnabledQuery({ coachOrgId }, { skip: !coachOrgId })
  const communityEnabledLoading = communityEnabledData === undefined || communityEnabledData?.isLoading

  const [activeTab, setActiveTab] = useState(TAB_ALL)
  // Newest first by default: a returning coach helps recent, still-engaged
  // members first. The toggle flips to oldest-first for strict queue-clearing.
  const [inboxNewestFirst, setInboxNewestFirst] = useState(true)
  const [confirmClear, setConfirmClear] = useState(null) // null | { postIds, label }

  const { data: postsData } = useListenCommunityPostsQuery({ coachOrgId }, { skip: !coachOrgId })
  const feedPager = usePostPager({
    liveData: postsData,
    getOrderValue: orderByCreatedAt,
    useOlderQuery: ({ cursor, limit, skip }) =>
      useListenOlderCommunityPostsQuery(
        { coachOrgId, endAtCreatedAt: cursor?.value, endAtKey: cursor?.id, limit },
        { skip: skip || !coachOrgId }
      ),
  })
  const postsLoading = feedPager.isLoading

  // The needs-reply queue has its own query on the server-maintained flag, so
  // the count and list are exact at any depth — not limited to the feed window.
  const { data: needsReplyData } = useListenNeedsReplyPostsQuery({ coachOrgId }, { skip: !coachOrgId })
  const needsReplyLoading = needsReplyData === undefined || needsReplyData?.isLoading
  const [markPostsDone] = useMarkPostsDoneMutation()

  // "Your posts" is its own per-author query too, so it is complete regardless
  // of the feed window.
  const { data: myPostsData } = useListenMyCommunityPostsQuery({ coachOrgId, userId }, { skip: !coachOrgId || !userId })
  const myPostsPager = usePostPager({
    liveData: myPostsData,
    getOrderValue: orderByKey,
    useOlderQuery: ({ cursor, limit, skip }) =>
      useListenOlderMyCommunityPostsQuery(
        { coachOrgId, userId, endAtKey: cursor?.id, limit },
        { skip: skip || !coachOrgId || !userId }
      ),
  })
  const myPostsLoading = myPostsPager.isLoading

  // Banned members' posts are hidden everywhere, as in the app. Waiting for
  // this map before rendering keeps a banned member's post from flashing in.
  const { data: bannedMembersData } = useListenBannedMembersQuery({ coachOrgId }, { skip: !coachOrgId })
  const bannedMembersLoading = bannedMembersData === undefined || bannedMembersData?.isLoading
  const bannedMemberIds = bannedMembersLoading || bannedMembersData === null ? {} : bannedMembersData

  const isLoading = postsLoading || needsReplyLoading || myPostsLoading || bannedMembersLoading

  const now = Date.now()
  const allPosts = filterVisiblePosts(Object.values(feedPager.posts), {}, bannedMemberIds)
  // Coach pins always outrank member pins; within each group, most recently
  // pinned first. Must match mobile's getVisiblePinnedPosts sort.
  const pinnedPosts = orderBy(
    allPosts.filter((post) => isPostPinned(post, now)),
    [(post) => (post?.authorIsCoach === true ? 1 : 0), (post) => post?.pinnedUntil || 0],
    ['desc', 'desc']
  )
  const feedPosts = orderBy(
    allPosts.filter((post) => !isPostPinned(post, now)),
    [(post) => post?.createdAt || 0],
    ['desc']
  )
  const needsReplyPosts = orderBy(
    filterVisiblePosts(
      Object.values(normalizeCommunityRecords(needsReplyLoading ? {} : needsReplyData)).filter(needsCoachReply),
      {},
      bannedMemberIds
    ),
    [(post) => post?.needsReplySince || 0],
    [inboxNewestFirst ? 'desc' : 'asc']
  )
  // Newest first, pinned or not — one flat list of everything the coach wrote.
  // The authorId check is belt and braces on top of the per-author query.
  const myPosts = orderBy(
    Object.values(myPostsPager.posts).filter((post) => post?.authorId === userId),
    [(post) => post?.createdAt || 0],
    ['desc']
  )
  const hasPosts = pinnedPosts.length > 0 || feedPosts.length > 0

  const handleConfirmClear = async () => {
    if (!confirmClear) return
    const result = await markPostsDone({ coachOrgId, postIds: confirmClear.postIds })
    setConfirmClear(null)
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not mark as done. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: 'Marked as done', type: 'success' })
  }

  const tabClasses = (isActive) =>
    `px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
      isActive ? 'bg-tGreen text-white' : 'bg-white text-tGray-dark hover:bg-gray-100'
    }`

  // Community is off for this org: the page becomes the pitch + the switch.
  // The same toggle stays available in Account → Advanced.
  if (!communityEnabledLoading && communityEnabledData !== true) {
    return <CommunityDisabled coachOrgId={coachOrgId} />
  }

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-16'>
      <div className='w-full max-w-[640px] mx-auto mt-12'>
        <div className='flex items-start justify-between mb-8'>
          <div className='mr-6'>
            <h1 className='font-bold text-5xl text-tBlack'>Community</h1>
            <p className='text-gray-500 mt-4'>Posts you create here show up for every member in the app.</p>
          </div>
          <div className='ml-6 shrink-0'>
            <Dialog>
              <DialogTrigger css={[buttonBase, buttonVariants.primary, buttonSizes.lg]} className='whitespace-nowrap'>
                New post
              </DialogTrigger>
              <DialogContent header='New post'>
                <FormRefsControlProvider>
                  <CommunityPostForm coachOrgId={coachOrgId} userId={userId} />
                </FormRefsControlProvider>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* The sort and "Mark all as done" controls wrap onto a line of their own,
            where they read as a header for the list rather than part of the pills. */}
        <div className='flex flex-wrap items-center gap-y-6 mb-6'>
          <button className={tabClasses(activeTab === TAB_ALL)} onClick={() => setActiveTab(TAB_ALL)}>
            All
          </button>
          <button className={`${tabClasses(activeTab === TAB_MINE)} ml-2`} onClick={() => setActiveTab(TAB_MINE)}>
            Your posts
          </button>
          {/* Count is only final once banned members have loaded, or it jumps. */}
          <button
            className={`${tabClasses(activeTab === TAB_NEEDS_REPLY)} ml-2`}
            onClick={() => setActiveTab(TAB_NEEDS_REPLY)}
          >
            Needs reply
            {!isLoading && needsReplyPosts.length > 0 ? ` · ${formatBadgeCount(needsReplyPosts.length)}` : ''}
          </button>
          {activeTab === TAB_NEEDS_REPLY && (
            <div className='flex items-center ml-auto pl-2'>
              {/* Same look as the Exercise Library's sortable column headers. */}
              <button
                className='inline-flex items-center whitespace-nowrap text-sm font-bold text-gray-500 hover:text-tGreen mr-4'
                onClick={() => setInboxNewestFirst(!inboxNewestFirst)}
              >
                {inboxNewestFirst ? 'Newest first' : 'Oldest first'}
                {inboxNewestFirst ? (
                  <FiChevronDown className='inline-block w-4 h-4 ml-0.5' />
                ) : (
                  <FiChevronUp className='inline-block w-4 h-4 ml-0.5' />
                )}
              </button>
              {needsReplyPosts.length > 0 && (
                <button
                  className='inline-flex items-center whitespace-nowrap text-sm font-semibold text-tGray-dark hover:text-tGreen'
                  onClick={() =>
                    setConfirmClear({
                      postIds: needsReplyPosts.map((post) => post.id),
                      label: 'Everything currently here',
                    })
                  }
                >
                  <BsCheck2All className='w-4 h-4 mr-1 shrink-0' /> Mark all as done
                </button>
              )}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className='mb-20'>
            {times(3, (index) => (
              <PostSkeleton key={index} />
            ))}
          </div>
        ) : activeTab === TAB_NEEDS_REPLY ? (
          <div className='mb-20'>
            {needsReplyPosts.map((post) => (
              <Post
                key={post.id}
                post={post}
                coachOrgId={coachOrgId}
                userId={userId}
                inboxMode
                onMarkOlderDone={(target) =>
                  setConfirmClear({
                    postIds: needsReplyPosts
                      .filter((p) => (p?.needsReplySince || 0) <= (target?.needsReplySince || 0))
                      .map((p) => p.id),
                    label: 'This post and everything older',
                  })
                }
              />
            ))}
            {needsReplyPosts.length === 0 && <EmptyStateContainer text="You're all caught up 🎉" />}
          </div>
        ) : activeTab === TAB_MINE ? (
          <div className='mb-20'>
            {myPosts.map((post) => (
              <Post key={post.id} post={post} coachOrgId={coachOrgId} userId={userId} />
            ))}
            {myPosts.length === 0 && <EmptyStateContainer text="You haven't posted yet" />}
            <LoadMoreButton pager={myPostsPager} />
          </div>
        ) : (
          <div className='mb-20'>
            {pinnedPosts.length > 0 && (
              <section className='mb-8'>
                <h2 className='flex items-center text-sm font-bold uppercase tracking-wider text-tBlack mb-4'>
                  <BsPinFill className='w-4 h-4 mr-2 text-tGreen' />
                  Pinned
                </h2>
                {pinnedPosts.map((post) => (
                  <Post key={post.id} post={post} coachOrgId={coachOrgId} userId={userId} />
                ))}
              </section>
            )}
            {feedPosts.length > 0 && (
              <section>
                {pinnedPosts.length > 0 && (
                  <h2 className='text-sm font-bold uppercase tracking-wider text-tBlack mb-4'>Recent</h2>
                )}
                {feedPosts.map((post) => (
                  <Post key={post.id} post={post} coachOrgId={coachOrgId} userId={userId} />
                ))}
              </section>
            )}
            {!hasPosts && <EmptyStateContainer text='No posts yet' />}
            <LoadMoreButton pager={feedPager} />
          </div>
        )}

        <Dialog open={Boolean(confirmClear)} setOpen={(open) => !open && setConfirmClear(null)}>
          <DialogContent>
            <CreateDialogBanner
              titleText='Mark as done?'
              createBtnText='Mark as done'
              text={`${confirmClear?.label ?? ''} will be cleared from the "Needs reply" list.`}
              subtext="Nothing is deleted, and all posts stay in the feed. A thread comes back if a member replies again. This can't be undone."
              descriptionClassNames='text-left space-y-2'
              handleCreate={handleConfirmClear}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

// Shown under a paged list while Firebase may still have older posts.
function LoadMoreButton({ pager }) {
  if (!pager.hasMore) return null
  return (
    <div className='flex justify-center mt-2'>
      <button
        className='px-5 py-2.5 rounded-lg text-sm font-semibold bg-white text-tGray-dark hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
        disabled={pager.isLoadingMore}
        onClick={pager.loadMore}
      >
        {pager.isLoadingMore ? 'Loading…' : 'Load more'}
      </button>
    </div>
  )
}

function CommunityDisabled({ coachOrgId }) {
  const [setCommunityEnabled, { isLoading: enabling }] = useSetCommunityEnabledMutation()

  return (
    <div className='flex flex-col items-center min-h-screen bg-offWhite px-4'>
      <div className='w-full max-w-xl bg-white rounded-lg shadow-sm border border-gray-100 mt-24 p-8'>
        <h1 className='font-bold text-3xl text-tBlack'>Community</h1>
        <p className='text-gray-500 mt-4'>
          Gives your paid subscribers a community feed in the app where they can post, reply, and like. They get push
          notifications for your posts and replies. Enabling also lets you send announcements to every paid subscriber.
        </p>
        <button
          className='mt-6 px-6 py-3 rounded-lg bg-tGreen text-white font-semibold disabled:opacity-50'
          disabled={enabling}
          onClick={() => setCommunityEnabled({ coachOrgId, enabled: true })}
        >
          Turn on Community
        </button>
      </div>
    </div>
  )
}

export default Community
